<script setup lang="ts">
import IconImport from "@/assets/icons/IconImport.vue";
import IconStars from "@/assets/icons/IconStars.vue";
import DatabaseTable from "@/components/modules/Database/DatabaseTable.vue";
import {useFilesStore} from "@/stores/files";
import {computed, onMounted, ref } from "vue";
import DatabaseToolbar from "@/components/modules/Database/DatabaseToolbar.vue";
import {storeToRefs} from "pinia";
import {useStore} from "@/stores";
import ForecastButton from "@/components/ui/buttons/ForecastButton.vue";
import FileReportView from "@/components/modules/FileReport/FileReportView.vue";
import {useReportStore} from "@/stores/report";
import GlobalLoader from "@/components/ui/loaders/GlobalLoader.vue";
import {FilesDataQuery, Region} from "@/types/files";

const reportStore = useReportStore();
const filesStore = useFilesStore();
const mainStore = useStore();

onMounted(() => {
  loadFilesData();
});

// Используем реактивные данные из store
const {filesData} = storeToRefs(filesStore);
const {currentReport} = storeToRefs(reportStore);

// Флаг загрузки файлов.

const { isLoadingFilesData } = storeToRefs(filesStore);

const selectedRegionList = ref<Region[]>([]);

// Активный месяц и год
const { selectedMonth } = storeToRefs(filesStore);
const { selectedYear } = storeToRefs(filesStore);

// Выбранный округ и регион из store
const { selectedDistrictKey, selectedRegionKey } = storeToRefs(filesStore);

const filesList = computed(() => {
  if (!filesData.value || typeof filesData.value !== "object") {
    return [];
  }
  return filesData.value.results || [];
});

// Функция загрузки данных
const loadFilesData = async () => {
  try {
    const params: FilesDataQuery = {};

    // Список выбранных регионов.
    params.regions = selectedRegionList.value;

    // Выбранный месяц, год.
    if (selectedMonth.value !== "all") {
      params.month = selectedMonth.value;
    }
    params.year = selectedYear.value;

    // Фильтр по округу
    if (selectedDistrictKey.value) {
      params.federal_districts = selectedDistrictKey.value;
    }

    // Фильтр по региону
    if (selectedRegionKey.value) {
      params.regions = selectedRegionKey.value;
    }

    await filesStore.getFilesData(params);
  } catch (error) {
    console.error("Ошибка загрузки данных файлов:", error);
  }
};

// Значение строки поиска параметров
const paramsSearchQueryValue = ref("");

// Отображение модального окна отчета.
const isShowReportView = ref(false);

const toggleShowReportView = () => {
  isShowReportView.value = !isShowReportView.value;
};

const createdReportId = ref<string | null>(null);

// Функция создания отчета.
const createReport = async () => {
  isStartReportLoading.value = true;
  const createdReport = await reportStore.createReport(selectedYear.value);
  createdReportId.value = createdReport.id;
  toggleShowReportView();
  isStartReportLoading.value = false;
};

const approveReport = async () => {
  if (createdReportId.value) {
    isStartReportApprove.value = true;
    await reportStore.approveReport(createdReportId.value);
    await reportStore.exportReport(selectedYear.value);
  }
  isStartReportApprove.value = false;
};

// Состояние создания отчета.

const isStartReportApprove = ref(false);
const isStartReportLoading = ref(false);
</script>

<template>
  <div class="database-view">
    <div class="database-view__header">
      <div class="database-view__title">
        База данных
      </div>
      <div class="database-view__control">
        <ButtonRoundedSecondary
          size="large"
          @click="createReport"
        >
          <IconImport />
          Создать отчет
        </ButtonRoundedSecondary>
        <ForecastButton
          :disabled="!mainStore.forecastStatus || filesData?.results.length === 0"
          :active="mainStore.forecastStatusDisplay"
          size="large"
          @click="mainStore.toggleForecastStatusDisplay()"
        >
          <IconStars />
          Прогноз 2025
        </ForecastButton>
      </div>
    </div>
    <div class="database-view__body">
      <DatabaseToolbar
        v-model:params-search-query="paramsSearchQueryValue"
        v-model:selected-regions="selectedRegionList"
        v-model:active-month="selectedMonth"
        v-model:active-year="selectedYear"
        v-model:selected-district="selectedDistrictKey"
        v-model:selected-region="selectedRegionKey"
        @update:active-month="loadFilesData"
        @update:selected-regions="loadFilesData"
        @update:active-year="loadFilesData"
        @update:selected-district="loadFilesData"
        @update:selected-region="loadFilesData"
      />
      <DatabaseTable
        :selected-regions="selectedRegionList"
        :params-search-query="paramsSearchQueryValue"
        :items="filesList"
        :loading="isLoadingFilesData"
        :selected-district-key="selectedDistrictKey"
        :selected-region-key="selectedRegionKey"
      />
    </div>

    <FileReportView
      v-if="isShowReportView && currentReport"
      :report="currentReport"
      :is-start-approve="isStartReportApprove"
      @approve-report="approveReport"
      @hide-report-view="toggleShowReportView"
    />
    <GlobalLoader v-if="isStartReportLoading" />
  </div>
</template>

<style scoped lang="scss">
.database-view {
  display: flex;
  flex-direction: column;
  gap: 20px;
  height: 100%;
  overflow: hidden;

  &__body {
    display: flex;
    flex-direction: column;
    gap: 10px;
    flex: 1;
    min-height: 0;
    overflow: hidden;
  }

  &__header {
    display: flex;
    justify-content: space-between;
    flex-shrink: 0;
  }

  &__title {
    font-size: 24px;
    font-weight: 500;
    line-height: 26px;
    color: black;
  }

  &__control {
    display: flex;
    gap: 10px;
  }
}
</style>